import { Component, HostListener, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthenticationService } from '@services/authentication.service'
import { SubscriptionService } from '@services/subscription.service'
import { UserService } from '@services/user.service'

type IconTone = 'danger' | 'warning' | 'muted'
type PrimaryKind = 'navigate' | 'billingPortal' | 'reactivate' | 'none'

type StaticContent = {
  title: string
  iconClass: string
  iconTone: IconTone
}

const staticContent: Record<string, StaticContent> = {
  '402': { title: 'Your subscription needs attention', iconClass: 'fa-credit-card', iconTone: 'danger' },
  '403': { title: 'Access Denied', iconClass: 'fa-lock', iconTone: 'muted' },
  '404': { title: 'Project Not Found', iconClass: 'fa-map-location-dot', iconTone: 'muted' },
}

const fallbackStatic: StaticContent = {
  title: 'Something went wrong',
  iconClass: 'fa-triangle-exclamation',
  iconTone: 'muted',
}

// Stripe statuses where a simple payment-method update recovers the sub.
// canceled / incomplete_expired are permanent; the only fix is to create a
// new subscription (admin-only).
const REACTIVATABLE_STATUSES = new Set<string>(['canceled', 'incomplete_expired'])

@Component({
  selector: 'shared-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.css'],
  standalone: false
})
export class ErrorPageComponent implements OnInit {
  public isOpening = false

  get errorCode() { return this.params.code }
  get params() { return this.route.snapshot.params }
  get user() { return this.userService.currentUser }

  get iconClass() { return (staticContent[this.errorCode] ?? fallbackStatic).iconClass }
  get iconTone(): IconTone { return (staticContent[this.errorCode] ?? fallbackStatic).iconTone }
  get title() { return (staticContent[this.errorCode] ?? fallbackStatic).title }

  get lead(): string {
    if (this.errorCode === '402') {
      if (this.subscriptionNeedsReactivation) {
        return 'Your organization\'s subscription has been canceled.'
      }
      return "Your organization's last payment didn't go through."
    }
    if (this.errorCode === '403') return "You don't have access to this part of the app."
    if (this.errorCode === '404') return "We couldn't find the page you were looking for."
    return "We couldn't complete that request."
  }

  get body(): string {
    if (this.errorCode === '402') {
      if (this.subscriptionNeedsReactivation) {
        return this.isAdmin
          ? 'Reactivate it to restore access — it will pick up the same plan and seats as before.'
          : 'Ask your organization admin to reactivate it, or email support@kartorium.com.'
      }
      return 'Update your payment method to restore access — it takes about a minute.'
    }
    if (this.errorCode === '403') {
      return 'Ask your organization administrator to grant you access, then come back.'
    }
    if (this.errorCode === '404') {
      return 'The link may have moved, or the project may have been deleted.'
    }
    return 'Try again in a moment.'
  }

  get hasPrimaryAction(): boolean { return this.primaryKind !== 'none' }
  get hasSecondaryAction(): boolean { return this.errorCode === '402' }

  get primaryActionLabel(): string {
    switch (this.primaryKind) {
      case 'billingPortal': return 'Update Payment Method'
      case 'reactivate': return 'Reactivate Subscription'
      case 'navigate': return 'Return to Dashboard'
      case 'none': return ''
    }
  }

  private get primaryKind(): PrimaryKind {
    if (this.errorCode === '402') {
      if (this.subscriptionNeedsReactivation) {
        return this.isAdmin ? 'reactivate' : 'none'
      }
      return 'billingPortal'
    }
    if (this.errorCode === '403' || this.errorCode === '404') return 'navigate'
    return 'navigate'
  }

  private get subscriptionNeedsReactivation(): boolean {
    const status = this.subscriptionService.details?.status
    return !!status && REACTIVATABLE_STATUSES.has(status)
  }

  private get isAdmin(): boolean {
    return this.authService.hasUserAdminAccess
  }

  // Where the Stripe Billing Portal should send the user when they click
  // "Return to Kartorium". Back to this same error page — ngOnInit + the
  // window:focus listener will then detect that the sub is active and
  // forward to /projects.
  private get portalReturnUrl(): string {
    return `${window.location.origin}/error/${this.errorCode}`
  }

  constructor(
    public userService: UserService,
    public route: ActivatedRoute,
    private router: Router,
    private authService: AuthenticationService,
    private subscriptionService: SubscriptionService,
  ) { }

  ngOnInit(): void {
    // On /error/402, the sub may have recovered since the page was cached —
    // either because the user just updated the card in the portal, or because
    // the page was refreshed after the webhook fired. Re-check and forward.
    if (this.errorCode === '402') {
      this.refreshAndMaybeRedirect()
    }
  }

  // Catches the user coming back to the Kartorium tab after finishing in the
  // Stripe portal: re-check status and forward to /projects if the sub is
  // active now.
  @HostListener('window:focus')
  onWindowFocus(): void {
    if (this.errorCode === '402') {
      this.refreshAndMaybeRedirect()
    }
  }

  private refreshAndMaybeRedirect(): void {
    this.subscriptionService.getSubscriptionDetails().subscribe({
      next: details => {
        if (details?.status === 'active' || details?.status === 'trialing') {
          this.router.navigate(['projects'])
        }
      },
      error: () => {},
    })
  }

  primaryAction() {
    switch (this.primaryKind) {
      case 'billingPortal':
        if (this.isOpening) return
        this.isOpening = true
        this.subscriptionService.openSubscriptionBillingPortal(this.portalReturnUrl).subscribe({
          next: () => { this.isOpening = false },
          error: () => { this.isOpening = false },
        })
        return
      case 'reactivate':
        if (this.isOpening) return
        this.isOpening = true
        this.subscriptionService.reactivateSubscription().subscribe({
          next: () => {
            this.isOpening = false
            this.router.navigate(['projects'])
          },
          error: () => { this.isOpening = false },
        })
        return
      case 'navigate':
        this.router.navigate(['projects'])
        return
      case 'none':
        return
    }
  }

  secondaryAction() {
    this.authService.logout()
  }
}
